/**
 * One thing Freya wants to write, held until the operator says so.
 *
 * Freya never saves on her own from the web chat: every write comes back as a
 * pending item with a preview. Confirm or Cancel goes to
 * `/coportal/chatbot/confirm`; the card is then replaced by Freya's result.
 * An expired item cannot be confirmed — the server would refuse it anyway,
 * so the buttons say so before anyone clicks.
 *
 * Shared by the floating widget, the coportal page and the Res Tools page.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { confirmFreya, type FreyaPending } from './freya.api';
import { SafeHtml } from './SafeHtml';

type Phase =
  | { kind: 'open' }
  | { kind: 'busy'; approve: boolean }
  | { kind: 'done'; html: string; approved: boolean }
  | { kind: 'error'; message: string };

function minutesLeft(expiresAt: string, now: number): number | null {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return null;
  return (t - now) / 60000;
}

function expiryLabel(mins: number | null, expiresAt: string): string | null {
  if (mins === null) return null;
  if (mins <= 0) return 'expired';
  if (mins < 1) return 'expires in under a minute';
  if (mins < 60) return `expires in ${Math.floor(mins)} min`;
  const d = new Date(expiresAt);
  return `expires at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export function FreyaPendingCard(props: { item: FreyaPending; threadId?: string | null }) {
  const { item, threadId } = props;
  const [phase, setPhase] = useState<Phase>({ kind: 'open' });
  const [now, setNow] = useState(() => Date.now());

  // Re-evaluate the countdown while the card can still be acted on.
  useEffect(() => {
    if (phase.kind === 'done' || !item.expiresAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, [phase.kind, item.expiresAt]);

  if (phase.kind === 'done') {
    return (
      <div
        data-testid="freya-result"
        style={{
          ...card,
          borderColor: phase.approved
            ? 'color-mix(in oklab, var(--c-ai) 40%, var(--border))'
            : 'var(--border)',
        }}
      >
        {phase.html ? (
          <SafeHtml html={phase.html} />
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>{phase.approved ? 'Done.' : 'Cancelled.'}</span>
        )}
      </div>
    );
  }

  const mins = minutesLeft(item.expiresAt, now);
  const expired = mins !== null && mins <= 0;
  const busy = phase.kind === 'busy';
  const label = expiryLabel(mins, item.expiresAt);

  async function act(approve: boolean) {
    setPhase({ kind: 'busy', approve });
    try {
      const html = await confirmFreya(item.id, approve, threadId);
      setPhase({ kind: 'done', html, approved: approve });
    } catch (err) {
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Freya could not finish that.',
      });
    }
  }

  return (
    <div role="group" aria-label="Needs your confirmation" style={card}>
      <span style={kicker}>
        needs your ok
        {label && (
          <span style={{ color: expired ? 'var(--heat)' : 'var(--text-dim)' }}> · {label}</span>
        )}
      </span>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <SafeHtml html={item.previewHtml} />
      </div>
      {phase.kind === 'error' && (
        <span role="alert" style={{ font: '500 11px/1.5 var(--hm-mono)', color: 'var(--heat)' }}>
          {phase.message}
        </span>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => void act(true)}
          disabled={busy || expired}
          style={{
            ...btn,
            background: 'color-mix(in oklab, var(--c-ai) 22%, var(--bg-elev))',
            borderColor: 'color-mix(in oklab, var(--c-ai) 50%, transparent)',
            opacity: busy || expired ? 0.5 : 1,
            cursor: busy || expired ? 'not-allowed' : 'pointer',
          }}
        >
          {busy && phase.approve ? 'Confirming…' : 'Confirm'}
        </button>
        <button
          type="button"
          onClick={() => void act(false)}
          disabled={busy}
          style={{
            ...btn,
            background: 'transparent',
            opacity: busy ? 0.5 : 1,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {busy && !phase.approve ? 'Cancelling…' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}

const card: CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: '10px 12px',
  font: '400 13px/1.5 var(--hm-display)',
  color: 'var(--text)',
  background: 'var(--bg-elev)',
  border: '1px solid color-mix(in oklab, var(--c-ai) 38%, var(--border))',
  borderRadius: 'var(--hm-radius-md)',
};

const kicker: CSSProperties = {
  font: '600 10px/1.4 var(--hm-mono)',
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--c-ai)',
};

const btn: CSSProperties = {
  padding: '6px 12px',
  font: '600 12px/1.2 var(--hm-display)',
  // Tinted plate, never a solid orange fill, so --text stays legible (ink rule).
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--hm-radius-sm)',
};
